import axios from 'axios';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || window.location.origin;
export const API = `${BACKEND_URL}/api`;
export { BACKEND_URL };

export const api = axios.create({
  baseURL: API,
  withCredentials: false,
});

export const TOKEN_KEY = 'cg_access_token';
export const REFRESH_KEY = 'cg_refresh_token';
export const USER_KEY = 'cg_user';

// ---------------------------------------------------------------------------
// Auth storage — sessionStorage, NOT localStorage
// ---------------------------------------------------------------------------
// We deliberately store the access/refresh tokens and the cached user
// document in `sessionStorage` so that auth is scoped to a single browser
// TAB. localStorage is shared across every tab of the same origin, which
// means opening a fresh tab would silently inherit the signed-in session of
// any other open tab — confusing and a real security risk for shared
// staff devices (a cashier walking up to an iPad would see the previous
// shift's session). sessionStorage gives us the right trade-off:
//   * persists across in-tab refreshes and route changes
//   * cleared automatically when the tab is closed
//   * isolated from other tabs of the same origin
// We still expose synchronous helpers so the rest of the app doesn't need
// to know which storage backend is in use.
const authStore = typeof window !== 'undefined' ? window.sessionStorage : null;

// One-shot migration — if the user was previously signed in (auth tokens
// stored in localStorage by an older build), wipe those legacy entries so
// the new "fresh tab = fresh session" contract holds immediately. Without
// this sweep, the very first tab the user opens after deploy would still
// auto-sign-in from the old localStorage payload.
if (typeof window !== 'undefined' && window.localStorage) {
  try {
    const legacy = [TOKEN_KEY, REFRESH_KEY, USER_KEY];
    if (legacy.some((k) => window.localStorage.getItem(k) != null)) {
      legacy.forEach((k) => window.localStorage.removeItem(k));
    }
  } catch (e) { /* private mode / disabled storage — non-fatal */ }
}

export function setAuth({ access_token, refresh_token, user }) {
  if (!authStore) return;
  if (access_token) authStore.setItem(TOKEN_KEY, access_token);
  if (refresh_token) authStore.setItem(REFRESH_KEY, refresh_token);
  if (user) authStore.setItem(USER_KEY, JSON.stringify(user));
}

export function clearAuth() {
  if (!authStore) return;
  authStore.removeItem(TOKEN_KEY);
  authStore.removeItem(REFRESH_KEY);
  authStore.removeItem(USER_KEY);
}

export function getAccessToken() {
  return authStore ? authStore.getItem(TOKEN_KEY) : null;
}

export function getRefreshToken() {
  return authStore ? authStore.getItem(REFRESH_KEY) : null;
}

export function getStoredUser() {
  try {
    const u = authStore ? authStore.getItem(USER_KEY) : null;
    return u ? JSON.parse(u) : null;
  } catch (e) {
    return null;
  }
}

// Setter used by pages that update the cached user record (e.g. Profile
// "Save changes", Loyalty refresh). Keeps the storage backend an
// implementation detail so callers don't have to know about session vs
// local storage.
export function setStoredUser(user) {
  if (!authStore || !user) return;
  authStore.setItem(USER_KEY, JSON.stringify(user));
}

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Silent access-token refresh
// ---------------------------------------------------------------------------
// Access tokens are short-lived (JWT_ACCESS_TTL_MIN, default 60). When the
// backend returns 401 we transparently exchange our long-lived refresh token
// for a fresh access token and retry the original request **once**. Only if
// the refresh itself fails (or there is no refresh token) do we surface the
// 401 and bounce the user to /login.
//
// A single in-flight refresh promise serialises concurrent 401s so we only
// hit /auth/refresh once even if a screen fires off 6 parallel requests.
// ---------------------------------------------------------------------------

let _refreshPromise = null;

function _refreshAccessToken() {
  if (_refreshPromise) return _refreshPromise;
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    _refreshPromise = Promise.reject(new Error('no_refresh_token'));
    return _refreshPromise;
  }
  // Bare axios call so the request doesn't carry the (expired) access token
  // and so a 401 here doesn't recurse into this interceptor.
  _refreshPromise = axios
    .post(`${API}/auth/refresh`, { refresh_token: refreshToken })
    .then((r) => {
      const newAccess = r?.data?.access_token;
      if (!newAccess) throw new Error('no_access_in_refresh_response');
      // Keep the refreshed access token in the SAME per-tab session store
      // we use everywhere else. Going back to localStorage here would leak
      // the freshly-rotated token across tabs.
      if (authStore) authStore.setItem(TOKEN_KEY, newAccess);
      return newAccess;
    })
    .catch((e) => {
      // Refresh failed (refresh token expired / invalid / user disabled).
      clearAuth();
      throw e;
    })
    .finally(() => {
      // Allow a subsequent 401 to trigger another refresh attempt later.
      setTimeout(() => { _refreshPromise = null; }, 0);
    });
  return _refreshPromise;
}

function _isAuthEndpoint(url = '') {
  return /\/auth\/(login|signup|refresh)\b/.test(url);
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const original = err?.config;

    // 401 on a non-auth request, not already retried, and we have a refresh token → try once.
    if (
      status === 401
      && original
      && !original._retry
      && !_isAuthEndpoint(original.url || '')
      && getRefreshToken()
    ) {
      original._retry = true;
      try {
        const newAccess = await _refreshAccessToken();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api.request(original);
      } catch (refreshErr) {
        // Bounce to login — but only if we're not already there.
        if (typeof window !== 'undefined') {
          const path = window.location.pathname;
          const isPublic = path.startsWith('/login')
            || path.startsWith('/signup')
            || path === '/'
            || path.startsWith('/menu')
            || path.startsWith('/pay/mm/')
            || path.startsWith('/table/')
            || path.startsWith('/checkin');
          if (!isPublic) {
            const next = encodeURIComponent(path + window.location.search);
            window.location.replace(`/login?next=${next}`);
          }
        }
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(err);
  },
);

export function getWsUrl() {
  const url = new URL(BACKEND_URL);
  const proto = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${url.host}/api/ws`;
}
